import { AZURE_AGENT_CONFIG } from '../../config/azureConfig';
import { getAzureToken } from './azureTokenService';

// Conversation substitui a antiga thread. Mantém o histórico do lado do serviço.
let currentConversationId = null;

const agentReference = () => ({
  name: AZURE_AGENT_CONFIG.agentName,
  type: 'agent_reference',
});

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/**
 * Cria uma conversation para manter o contexto entre turnos
 */
const createConversation = async () => {
  const token = await getAzureToken();
  const endpoint = `${AZURE_AGENT_CONFIG.projectEndpoint}/openai/v1/conversations`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erro ao criar conversation: ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  return data.id;
};

/**
 * Garante que existe uma conversation ativa
 */
const ensureConversation = async () => {
  if (!currentConversationId) {
    currentConversationId = await createConversation();
  }
  return currentConversationId;
};

/**
 * Monta o corpo da chamada de response
 */
const buildResponseBody = (conversationId, input, stream) => ({
  input,
  conversation: conversationId,
  agent_reference: agentReference(),
  ...(stream ? { stream: true } : {}),
});

/**
 * Extrai o texto da resposta a partir dos output items
 */
const extractOutputText = (data) => {
  if (typeof data.output_text === 'string' && data.output_text.trim()) {
    return data.output_text;
  }

  const items = Array.isArray(data.output) ? data.output : [];
  const texts = [];

  for (const item of items) {
    if (item.type !== 'message' || !Array.isArray(item.content)) continue;
    for (const part of item.content) {
      const text = typeof part.text === 'string' ? part.text : part.text?.value;
      if (text) texts.push(text);
    }
  }

  return texts.join('').trim();
};

/**
 * Envia uma mensagem para o agente e retorna a resposta completa
 */
export const sendChatMessage = async (messages) => {
  try {
    const conversationId = await ensureConversation();
    const userMessage = messages[messages.length - 1];
    const input =
      typeof userMessage.content === 'string'
        ? userMessage.content.trim()
        : String(userMessage.content);

    const endpoint = `${AZURE_AGENT_CONFIG.projectEndpoint}/openai/v1/responses`;
    const token = await getAzureToken();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(buildResponseBody(conversationId, input, false)),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erro ao gerar resposta:', errorText);
      throw new Error(`Erro ao gerar resposta: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    if (data.status === 'failed') {
      throw new Error(data.error?.message || 'Execução do agente falhou');
    }

    const content = extractOutputText(data);
    if (!content) {
      throw new Error('Resposta vazia do agente');
    }

    return {
      content,
      usage: {
        prompt_tokens: data.usage?.input_tokens ?? 0,
        completion_tokens: data.usage?.output_tokens ?? 0,
        total_tokens: data.usage?.total_tokens ?? 0,
      },
      finish_reason: 'stop',
    };
  } catch (error) {
    console.error('Erro ao enviar mensagem para o agente:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Erro desconhecido ao comunicar com o agente'
    );
  }
};

/**
 * Envia uma mensagem e entrega os deltas conforme o agente gera (SSE)
 */
export const sendStreamingChatMessage = async (
  messages,
  onContent,
  onComplete,
  onError,
  abortSignal = null
) => {
  try {
    const conversationId = await ensureConversation();
    const userMessage = messages[messages.length - 1];
    const input =
      typeof userMessage.content === 'string'
        ? userMessage.content.trim()
        : String(userMessage.content);

    const endpoint = `${AZURE_AGENT_CONFIG.projectEndpoint}/openai/v1/responses`;
    const token = await getAzureToken();

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(buildResponseBody(conversationId, input, true)),
      signal: abortSignal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erro ao gerar resposta:', errorText);
      onError(new Error(`Erro ao gerar resposta: ${response.status} - ${errorText}`));
      return;
    }

    if (!response.body) {
      onError(new Error('Streaming não suportado neste ambiente'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let assistantContent = '';
    let usage = null;
    let streamError = null;

    // SSE: eventos separados por linha em branco, dados nas linhas "data: ..."
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Normaliza CRLF: sem isso a divisão por linha em branco nunca casa
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';

      for (const chunk of chunks) {
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data:')) continue;

          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;

          let event;
          try {
            event = JSON.parse(payload);
          } catch {
            continue; // evento parcial ou keep-alive
          }

          if (event.type === 'response.output_text.delta' && event.delta) {
            assistantContent += event.delta;
            onContent(event.delta);
          } else if (event.type === 'response.completed') {
            usage = event.response?.usage || null;
            if (!assistantContent && event.response) {
              assistantContent = extractOutputText(event.response);
            }
          } else if (event.type === 'response.failed' || event.type === 'error') {
            streamError = event.response?.error?.message || event.message || 'Execução do agente falhou';
          }
        }
      }
    }

    if (streamError) {
      onError(new Error(streamError));
      return;
    }

    if (!assistantContent) {
      onError(new Error('Resposta vazia do agente'));
      return;
    }

    onComplete({
      content: assistantContent,
      usage: {
        prompt_tokens: usage?.input_tokens ?? 0,
        completion_tokens: usage?.output_tokens ?? 0,
        total_tokens: usage?.total_tokens ?? 0,
      },
      finish_reason: 'stop',
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      onError(new Error('Requisição cancelada'));
      return;
    }
    console.error('Erro ao enviar mensagem de streaming para o agente:', error);
    onError(
      error instanceof Error ? error : new Error('Erro desconhecido ao comunicar com o agente')
    );
  }
};

/**
 * Descarta a conversation atual (novo contexto no próximo envio)
 */
export const resetConversation = () => {
  currentConversationId = null;
};

export default {
  sendChatMessage,
  sendStreamingChatMessage,
  resetConversation,
};
